import { useState, useEffect, useCallback, useMemo } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useAuth, API } from "../App";
import { useJsonLd, buildRouteBreadcrumbJsonLd } from "../lib/seo";
import axios from "axios";
import { toast } from "sonner";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import PrivacyCheckbox from "../components/PrivacyCheckbox";
import TermsCheckbox from "../components/TermsCheckbox";
import FlightDetailsFields, { buildFlightDetailsPayload } from "../components/FlightDetailsFields";
import VeniceSubPicker, { VENICE_LABEL, VENICE_PIAZZALE, VENICE_AIRPORT, VENICE_NEAR, VENICE_WATER_TAXI, VENICE_HOTEL, isVeniceVariant, veniceDisplayLabel } from "../components/VeniceSubPicker";
import SpecificAddressField from "../components/SpecificAddressField";
import GuestRegistrationPrompt from "../components/GuestRegistrationPrompt";
import SiteNav from "../components/SiteNav";
import { useThemeTexts } from "../components/ThemeProvider";
import { renderMarkdownSafe } from "../lib/safeMarkdown";
import { isDateSelectable, filterSlotsForDate, earliestPickupLabel } from "../lib/bookingCutoff";
import { isValidInternationalPhone, INTERNATIONAL_PHONE_HINT } from "../lib/phoneValidation";
import { extractApiError } from "../lib/apiError";
import { format } from "date-fns";
import { 
  CalendarIcon, 
  MapPin, 
  Clock, 
  Users, 
  Briefcase, 
  Car, 
  Moon, 
  ChevronLeft,
  ArrowRight,
  ArrowLeftRight,
  CheckCircle,
  Star,
  Phone,
  Mail,
  LogOut,
  User,
  Menu,
  X,
  Repeat,
  Percent,
  Shield,
  Sparkles
} from "lucide-react";

// Background images based on category
const CATEGORY_IMAGES = {
  "Dolomites": "https://images.unsplash.com/photo-1612566166292-d39b2357f0bb?w=1200",
  "Cruise Terminals": "https://images.unsplash.com/photo-1618483129938-deb4e090f328?w=1200",
  "Cities": "https://images.unsplash.com/photo-1758894877630-bcbaad7685a9?w=1200",
  "Lakes": "https://images.unsplash.com/photo-1596786232605-3a1a72d4d40b?w=1200",
  "Tuscany": "https://images.unsplash.com/photo-1534445867742-43195f401b6c?w=1200",
  "International": "https://images.unsplash.com/photo-1516483638261-f4dbaf036963?w=1200",
  "Airports": "https://images.unsplash.com/photo-1436491865332-7a61a109cc05?w=1200",
  "Tours": "https://images.unsplash.com/photo-1612566166292-d39b2357f0bb?w=1200",
  "Local": "https://images.unsplash.com/photo-1523906834658-6e24ef2386f9?w=1200",
  "Riviera": "https://images.unsplash.com/photo-1537996194471-e657df975ab4?w=1200"
};

const TIME_INTERVAL_MINUTES = 30;
const TIME_SLOTS = [];
for (let h = 0; h < 24; h++) {
  for (let m = 0; m < 60; m += TIME_INTERVAL_MINUTES) {
    const hour = h.toString().padStart(2, '0');
    const minute = m.toString().padStart(2, '0');
    TIME_SLOTS.push(`${hour}:${minute}`);
  }
}

export default function RouteLandingPage() {
  const { slug } = useParams();
  const { user, login, logout } = useAuth();
  const navigate = useNavigate();
  const t = useThemeTexts();
  const [route, setRoute] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [pricePreview, setPricePreview] = useState(null);
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [returnDatePickerOpen, setReturnDatePickerOpen] = useState(false);
  const [showGuestPrompt, setShowGuestPrompt] = useState(false);
  const [pendingSubmit, setPendingSubmit] = useState(null);
  const [isInverted, setIsInverted] = useState(false);
  // Per-side Venice picks (each side independently chooses Piazzale Roma / Airport / Near Venice
  // when the route involves Venice on that side). Critical for Venice → Venice routes where
  // both sides must be individually chosen.
  const [venicePickOrigin, setVenicePickOrigin] = useState("");
  const [venicePickDest, setVenicePickDest] = useState("");
  const [originNearAddress, setOriginNearAddress] = useState("");
  const [destNearAddress, setDestNearAddress] = useState("");
  // Specific address for non-Venice sides
  const [originOtherAddress, setOriginOtherAddress] = useState("");
  const [destOtherAddress, setDestOtherAddress] = useState("");
  const [bookingSettings, setBookingSettings] = useState(null);
  const [fleet, setFleet] = useState({ vehicles: [], max_passengers: 12, max_luggage: 12 });
  const [luxuryOptions, setLuxuryOptions] = useState([]);
  const [luxuryDetail, setLuxuryDetail] = useState(null);
  // Hotel-in-Venice support on the single-route form.
  const [veniceHotels, setVeniceHotels] = useState([]);
  const [veniceHotelId, setVeniceHotelId] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    pickupDate: null,
    pickupTime: "10:00",
    returnDate: null,
    returnTime: "10:00",
    passengers: 2,
    luggage: 1,
    isLuxury: false,
    luxuryOptionIds: [],
    isRoundTrip: false,
    userName: user?.name || "",
    userEmail: user?.email || "",
    userPhone: "",
    notes: "",
    privacyAccepted: false,
    termsAccepted: false,
    flightDetails: null
  });

  const fetchRoute = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/routes/by-slug/${slug}`);
      setRoute(response.data);
    } catch (error) {
      toast.error("Route not found");
      navigate("/routes");
    } finally {
      setLoading(false);
    }
  }, [slug, navigate]);

  const fetchBookingSettings = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/booking-settings`);
      setBookingSettings(response.data);
    } catch (error) {
      console.error("Failed to fetch booking settings", error);
    }
  }, []);

  const calculatePrice = useCallback(async () => {
    if (!route) return;
    // Substitute each Venice side with the customer's chosen variant so the
    // backend can detect water-taxi / airport / near-Venice surcharges. If
    // the user hasn't picked a sub-option yet, fall back to the raw DB origin
    // (Piazzale Roma) — that matches existing pricing.
    const originIsVenice = isVeniceVariant(route.origin);
    const destIsVenice = isVeniceVariant(route.destination);
    const origin = originIsVenice && venicePickOrigin ? venicePickOrigin : route.origin;
    const destination = destIsVenice && venicePickDest ? venicePickDest : route.destination;
    try {
      const response = await axios.post(`${API}/calculate-price`, {
        origin,
        destination,
        pickup_time: formData.pickupTime,
        is_luxury: formData.isLuxury,
        luxury_option_ids: formData.luxuryOptionIds,
        passengers: formData.passengers,
        route_id: route.route_id,
        is_round_trip: formData.isRoundTrip,
        return_time: formData.returnTime,
        venice_hotel_id:
          (originIsVenice && venicePickOrigin === VENICE_HOTEL) ||
          (destIsVenice && venicePickDest === VENICE_HOTEL)
            ? veniceHotelId
            : null,
      });
      setPricePreview(response.data);
    } catch (error) {
      console.warn("Price preview failed", error?.response?.status);
    }
  }, [route, venicePickOrigin, venicePickDest, veniceHotelId, formData.pickupTime, formData.isLuxury, formData.luxuryOptionIds, formData.passengers, formData.isRoundTrip, formData.returnTime]);

  useEffect(() => {
    fetchRoute();
    fetchBookingSettings();
    axios.get(`${API}/luxury-options`).then((r) => setLuxuryOptions(r.data || [])).catch(() => {});
    axios.get(`${API}/vehicles`).then((r) => setFleet(r.data || { vehicles: [], max_passengers: 12, max_luggage: 12 })).catch(() => {});
    axios.get(`${API}/venice-hotels`).then((r) => setVeniceHotels(r.data || [])).catch(() => {});
  }, [fetchRoute, fetchBookingSettings]);

  // Snap pickup time to first valid slot when date/settings change
  useEffect(() => {
    if (!bookingSettings || !formData.pickupDate) return;
    const valid = filterSlotsForDate(TIME_SLOTS, bookingSettings, formData.pickupDate);
    if (valid.length === 0) return;
    if (!valid.includes(formData.pickupTime)) {
      setFormData(prev => ({ ...prev, pickupTime: valid[0] }));
    }
  }, [bookingSettings, formData.pickupDate, formData.pickupTime]);

  useEffect(() => {
    if (user) {
      setFormData(prev => ({
        ...prev,
        userName: user.name || "",
        userEmail: user.email || ""
      }));
    }
  }, [user]);

  useEffect(() => {
    if (route && formData.pickupTime) {
      calculatePrice();
    }
  }, [route, formData.pickupTime, formData.isLuxury, formData.isRoundTrip, formData.returnTime, formData.passengers, formData.luxuryOptionIds, calculatePrice]);

  // SEO: Set document title and meta tags. Public-facing labels collapse "Venice - Piazzale Roma" → "Venice".
  useEffect(() => {
    if (route) {
      const dispOrigin = veniceDisplayLabel(route.origin);
      const dispDest = veniceDisplayLabel(route.destination);
      // If admin saved meta_title with a Venice variant, replace with the friendly "Venice" label
      const friendlyTitle = (route.meta_title || `${dispOrigin} to ${dispDest} Private Transfer | TaxiNearMe`)
        .replaceAll(VENICE_PIAZZALE, VENICE_LABEL).replaceAll(VENICE_AIRPORT, VENICE_LABEL);
      document.title = friendlyTitle;
      const setMeta = (name, content) => {
        let el = document.querySelector(`meta[name="${name}"]`) || document.querySelector(`meta[property="${name}"]`);
        if (!el) { el = document.createElement('meta'); el.setAttribute(name.startsWith('og:') ? 'property' : 'name', name); document.head.appendChild(el); }
        el.setAttribute('content', content);
      };
      const friendlyDesc = (route.meta_description || '').replaceAll(VENICE_PIAZZALE, VENICE_LABEL).replaceAll(VENICE_AIRPORT, VENICE_LABEL);
      setMeta('description', friendlyDesc);
      setMeta('keywords', route.keywords || '');
      setMeta('og:title', friendlyTitle);
      setMeta('og:description', friendlyDesc);
      setMeta('og:type', 'website');
      let canonical = document.querySelector('link[rel="canonical"]');
      if (!canonical) { canonical = document.createElement('link'); canonical.setAttribute('rel', 'canonical'); document.head.appendChild(canonical); }
      canonical.setAttribute('href', `https://taxinearme.it/route/${route.slug}`);

      // Schema.org JSON-LD (GEO / AI indexing) — uses admin override when set,
      // otherwise the backend-generated default (TaxiService + LocalBusiness).
      const jsonLd = route.json_ld_effective || route.json_ld;
      let ldScript = document.getElementById('tnm-route-jsonld');
      if (jsonLd) {
        if (!ldScript) {
          ldScript = document.createElement('script');
          ldScript.setAttribute('type', 'application/ld+json');
          ldScript.setAttribute('id', 'tnm-route-jsonld');
          document.head.appendChild(ldScript);
        }
        ldScript.textContent = jsonLd;
      } else if (ldScript) {
        ldScript.remove();
      }
    }
    return () => {
      document.title = 'TaxiNearMe';
      const ldScript = document.getElementById('tnm-route-jsonld');
      if (ldScript) ldScript.remove();
    };
  }, [route]);

  // Additional BreadcrumbList JSON-LD (Home → Routes → [Origin → Destination]).
  const breadcrumbJsonLd = useMemo(
    () => buildRouteBreadcrumbJsonLd({ route }),
    [route]
  );
  useJsonLd(breadcrumbJsonLd);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!route || !formData.pickupDate || !formData.userName || !formData.userEmail || !formData.userPhone) {
      toast.error("Please fill in all required fields");
      return;
    }

    if (!isValidInternationalPhone(formData.userPhone)) {
      toast.error(INTERNATIONAL_PHONE_HINT);
      return;
    }

    if (formData.isRoundTrip && !formData.returnDate) {
      toast.error("Please select a return date");
      return;
    }

    if (!formData.privacyAccepted) {
      toast.error("Please accept the Privacy Policy");
      return;
    }

    if (!formData.termsAccepted) {
      toast.error("Please accept the Terms of Service and Cancellation Policy");
      return;
    }

    const rawOrigin = isInverted ? route.destination : route.origin;
    const rawDestination = isInverted ? route.origin : route.destination;
    const originIsVenice = isVeniceVariant(rawOrigin);
    const destIsVenice = isVeniceVariant(rawDestination);
    // Each Venice side independently needs a sub-pick (Piazzale / Airport / Near Venice)
    if (originIsVenice && !venicePickOrigin) {
      toast.error("Please choose Piazzale Roma, Airport or Near Venice for the pickup side");
      return;
    }
    if (destIsVenice && !venicePickDest) {
      toast.error("Please choose Piazzale Roma, Airport or Near Venice for the drop-off side");
      return;
    }
    // Venice → Venice: cannot be the same variant on both sides
    if (originIsVenice && destIsVenice && venicePickOrigin === venicePickDest) {
      toast.error("Pickup and drop-off cannot be the same Venice point");
      return;
    }
    // Required near-Venice / water-taxi addresses
    if (venicePickOrigin === VENICE_NEAR && !originNearAddress.trim()) {
      toast.error("Please enter the pickup address near Venice (within 15 km)");
      return;
    }
    if (venicePickDest === VENICE_NEAR && !destNearAddress.trim()) {
      toast.error("Please enter the drop-off address near Venice (within 15 km)");
      return;
    }
    if (venicePickOrigin === VENICE_WATER_TAXI && !originNearAddress.trim()) {
      toast.error("Please enter the pickup hotel/address in Venice City Center (water taxi)");
      return;
    }
    if (venicePickDest === VENICE_WATER_TAXI && !destNearAddress.trim()) {
      toast.error("Please enter the drop-off hotel/address in Venice City Center (water taxi)");
      return;
    }
    if ((venicePickOrigin === VENICE_HOTEL || venicePickDest === VENICE_HOTEL) && !veniceHotelId) {
      toast.error("Please pick a hotel from the list");
      return;
    }

    // Substitute each Venice side with the customer's chosen variant
    const origin = originIsVenice ? venicePickOrigin : rawOrigin;
    const destination = destIsVenice ? venicePickDest : rawDestination;

    // Build specific address per side (shared input feeds either NEAR or WATER_TAXI)
    let originAddr = "";
    let destAddr = "";
    const originWantsAddress = venicePickOrigin === VENICE_NEAR || venicePickOrigin === VENICE_WATER_TAXI;
    const destWantsAddress = venicePickDest === VENICE_NEAR || venicePickDest === VENICE_WATER_TAXI;
    if (originIsVenice && originWantsAddress) originAddr = originNearAddress.trim();
    else if (!originIsVenice && originOtherAddress.trim()) originAddr = originOtherAddress.trim();
    if (destIsVenice && destWantsAddress) destAddr = destNearAddress.trim();
    else if (!destIsVenice && destOtherAddress.trim()) destAddr = destOtherAddress.trim();

    // Stash the resolved values so the modal-driven submission can reuse them
    setPendingSubmit({ origin, destination, originAddr, destAddr });

    // Guests see the registration prompt first; logged-in users go straight in.
    if (!user) {
      setShowGuestPrompt(true);
      return;
    }

    await performSubmit({ origin, destination, originAddr, destAddr, thenLogin: false });
  };

  const performSubmit = async ({ origin, destination, originAddr, destAddr, thenLogin }) => {
    setSubmitting(true);
    try {
      const response = await axios.post(`${API}/quotes`, {
        user_email: formData.userEmail,
        user_name: formData.userName,
        user_phone: formData.userPhone,
        origin: origin,
        destination: destination,
        origin_specific_address: originAddr || null,
        destination_specific_address: destAddr || null,
        route_id: route.route_id,
        pickup_date: format(formData.pickupDate, "yyyy-MM-dd"),
        pickup_time: formData.pickupTime,
        passengers: formData.passengers,
        luggage: formData.luggage,
        is_luxury: formData.isLuxury,
        luxury_option_ids: formData.luxuryOptionIds,
        is_round_trip: formData.isRoundTrip,
        return_date: formData.isRoundTrip && formData.returnDate ? format(formData.returnDate, "yyyy-MM-dd") : null,
        return_time: formData.isRoundTrip ? formData.returnTime : null,
        notes: formData.notes + (isInverted ? " [Return direction]" : ""),
        privacy_accepted: formData.privacyAccepted,
        terms_accepted: formData.termsAccepted,
        flight_details: buildFlightDetailsPayload(formData.flightDetails),
        venice_hotel_id:
          origin === VENICE_HOTEL || destination === VENICE_HOTEL ? veniceHotelId : null,
      });

      setShowGuestPrompt(false);
      toast.success("Quote request submitted!");
      if (thenLogin) {
        try { sessionStorage.setItem("pending_quote_id", response.data.quote_id); } catch (e) { console.warn("sessionStorage set failed (private mode?)", e); }
        login();
        return;
      }
      navigate(`/quote-confirmation/${response.data.quote_id}`);
    } catch (error) {
      console.error("Route quote submit failed:", error?.response?.status, error?.response?.data, error);
      toast.error(extractApiError(error, "Failed to submit quote request"));
    } finally {
      setSubmitting(false);
    }
  };

  const isNightService = () => {
    if (!formData.pickupTime) return false;
    const hour = parseInt(formData.pickupTime.split(":")[0]);
    return hour >= 20 || hour < 7;
  };

  const rawDisplayOrigin = isInverted ? route?.destination : route?.origin;
  const rawDisplayDestination = isInverted ? route?.origin : route?.destination;
  const displayOrigin = veniceDisplayLabel(rawDisplayOrigin);
  const displayDestination = veniceDisplayLabel(rawDisplayDestination);
  const originIsVenice = isVeniceVariant(rawDisplayOrigin);
  const destIsVenice = isVeniceVariant(rawDisplayDestination);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  if (!route) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="text-center">
          <p className="text-white/50 mb-4">Route not found</p>
          <Link to="/routes">
            <Button className="btn-gold">View All Routes</Button>
          </Link>
        </div>
      </div>
    );
  }

  const customImage = route.header_image_url
    ? (route.header_image_url.startsWith("http")
        ? route.header_image_url
        : `${process.env.REACT_APP_BACKEND_URL}${route.header_image_url}`)
    : null;
  const bgImage = customImage || CATEGORY_IMAGES[route.category] || CATEGORY_IMAGES["Cities"];

  return (
    <div className="min-h-screen bg-[#050505]">
      {/* Guest registration prompt (only fires when !user and they hit submit) */}
      <GuestRegistrationPrompt
        open={showGuestPrompt}
        onClose={() => setShowGuestPrompt(false)}
        onContinueAsGuest={() => pendingSubmit && performSubmit({ ...pendingSubmit, thenLogin: false })}
        onSignInAndSubmit={() => pendingSubmit && performSubmit({ ...pendingSubmit, thenLogin: true })}
        submitting={submitting}
      />
      {/* Navigation — shared component (includes All Routes dropdown submenu) */}
      <SiteNav />

      {/* Hero Section with Route Info */}
      <section 
        className="relative pt-20 min-h-[50vh] flex items-end"
        style={{ 
          backgroundImage: `url(${bgImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/70 to-transparent" />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12 w-full">
          <Link to="/routes" className="inline-flex items-center gap-2 text-white/50 hover:text-[#C5A059] transition-colors mb-4">
            <ChevronLeft className="w-4 h-4" />
            <span className="text-sm">All Routes</span>
          </Link>
          
          {/* SEO H1 Heading */}
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
            {(route.h1_heading || `${displayOrigin} to ${displayDestination} Private Transfer`)
              .replaceAll(VENICE_PIAZZALE, VENICE_LABEL)
              .replaceAll(VENICE_AIRPORT, VENICE_LABEL)}
          </h1>
          
          <p className="text-white/60 text-lg max-w-2xl mb-6">
            {route.meta_description?.split('.')[0]?.replaceAll(VENICE_PIAZZALE, VENICE_LABEL).replaceAll(VENICE_AIRPORT, VENICE_LABEL)}.
          </p>

          {/* Quick Info */}
          <div className="flex flex-wrap items-center gap-6 text-sm">
            <div className="flex items-center gap-2 text-white/70">
              <Clock className="w-4 h-4 text-[#C5A059]" />
              <span>~{route.duration_hours}h journey</span>
            </div>
            <div className="flex items-center gap-2 text-white/70">
              <MapPin className="w-4 h-4 text-[#C5A059]" />
              <span>{route.distance_km} km</span>
            </div>
            <div className="flex items-center gap-2 text-white/70">
              <Users className="w-4 h-4 text-[#C5A059]" />
              <span>2-7 passengers</span>
            </div>
            <div className="flex items-center gap-2 text-white/70">
              <Car className="w-4 h-4 text-[#C5A059]" />
              <span>Mercedes V-Class</span>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-12 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-5 gap-8">
          {/* Left Content - Route Details */}
          <div className="lg:col-span-3 space-y-8">
            {/* Route Card with Swap */}
            <div className="bg-[#121212] border border-white/5 rounded-sm p-6" data-testid="route-card">
              <h2 className="text-xl font-semibold text-white mb-6">Your Route</h2>
              
              <div className="flex items-center justify-between gap-4 p-4 bg-[#1F1F1F] rounded-sm">
                <div className="flex items-center gap-4 flex-1">
                  <MapPin className="w-6 h-6 text-[#C5A059]" />
                  <span className="text-xl text-white">{displayOrigin}</span>
                </div>
                
                {/* Swap Button */}
                <button
                  onClick={() => setIsInverted(!isInverted)}
                  className={`p-3 rounded-full transition-all duration-300 hover:bg-[#C5A059]/20 ${
                    isInverted ? 'bg-[#C5A059]/20' : 'bg-white/5'
                  }`}
                  title="Swap direction"
                  data-testid="swap-direction"
                >
                  <ArrowLeftRight className={`w-6 h-6 transition-colors ${
                    isInverted ? 'text-[#C5A059]' : 'text-white/50'
                  }`} />
                </button>
                
                <div className="flex items-center gap-4 flex-1 justify-end">
                  <span className="text-xl text-white font-semibold">{displayDestination}</span>
                  <MapPin className="w-6 h-6 text-[#C5A059]" />
                </div>
              </div>

              {isInverted && (
                <div className="mt-3 text-center">
                  <span className="text-[#C5A059] text-sm uppercase tracking-wide">Return Direction</span>
                </div>
              )}

              {/* Venice variant sub-picker(s) — one per side that touches Venice.
                  For a Venice → Venice route, BOTH pickers render. */}
              {(originIsVenice || destIsVenice) && (
                <div className="mt-5 pt-5 border-t border-white/10 space-y-5">
                  {originIsVenice && (
                    <div data-testid="route-venice-origin-wrap">
                      <p className="text-white/60 text-xs uppercase tracking-wide mb-3">
                        Pickup point (Venice)
                      </p>
                      <VeniceSubPicker
                        value={venicePickOrigin || VENICE_LABEL}
                        onSelect={(v) => {
                          setVenicePickOrigin(v);
                          if (v !== VENICE_NEAR && v !== VENICE_WATER_TAXI) setOriginNearAddress("");
                          if (v !== VENICE_HOTEL && venicePickDest !== VENICE_HOTEL) setVeniceHotelId(null);
                        }}
                        testIdPrefix="route-venice-origin"
                        label=""
                        nearAddress={originNearAddress}
                        onChangeNearAddress={setOriginNearAddress}
                        disabledVariant={destIsVenice && venicePickDest ? venicePickDest : null}
                        showWaterTaxi={!!bookingSettings?.water_taxi_enabled}
                        showHotel={
                          // Origin picker: Hotel-as-pickup is allowed when the OTHER
                          // side (drop-off) is Airport/Near (airport_override) OR a
                          // non-Venice location (surcharge mode).
                          destIsVenice
                            ? (venicePickDest === VENICE_AIRPORT || venicePickDest === VENICE_NEAR)
                            : true
                        }
                        hotelOptions={veniceHotels}
                        selectedHotelId={venicePickOrigin === VENICE_HOTEL ? veniceHotelId : null}
                        onSelectHotel={(hid) => setVeniceHotelId(hid)}
                      />
                      {venicePickOrigin && venicePickOrigin !== VENICE_NEAR && venicePickOrigin !== VENICE_WATER_TAXI && (
                        <p className="mt-3 text-[#C5A059] text-sm" data-testid="route-venice-origin-pick-label">
                          Pickup: <strong>{venicePickOrigin}</strong>
                        </p>
                      )}
                    </div>
                  )}
                  {destIsVenice && (
                    <div data-testid="route-venice-dest-wrap">
                      <p className="text-white/60 text-xs uppercase tracking-wide mb-3">
                        Drop-off point (Venice)
                      </p>
                      <VeniceSubPicker
                        value={venicePickDest || VENICE_LABEL}
                        onSelect={(v) => {
                          setVenicePickDest(v);
                          if (v !== VENICE_NEAR && v !== VENICE_WATER_TAXI) setDestNearAddress("");
                          if (v !== VENICE_HOTEL && venicePickOrigin !== VENICE_HOTEL) setVeniceHotelId(null);
                        }}
                        testIdPrefix="route-venice-dest"
                        label=""
                        nearAddress={destNearAddress}
                        onChangeNearAddress={setDestNearAddress}
                        disabledVariant={originIsVenice && venicePickOrigin ? venicePickOrigin : null}
                        showWaterTaxi={!!bookingSettings?.water_taxi_enabled}
                        showHotel={
                          // Destination picker: Hotel-as-drop-off is allowed when the
                          // OTHER side (pickup) is Airport/Near (airport_override) OR
                          // a non-Venice location (surcharge mode).
                          originIsVenice
                            ? (venicePickOrigin === VENICE_AIRPORT || venicePickOrigin === VENICE_NEAR)
                            : true
                        }
                        hotelOptions={veniceHotels}
                        selectedHotelId={venicePickDest === VENICE_HOTEL ? veniceHotelId : null}
                        onSelectHotel={(hid) => setVeniceHotelId(hid)}
                      />
                      {venicePickDest && venicePickDest !== VENICE_NEAR && venicePickDest !== VENICE_WATER_TAXI && (
                        <p className="mt-3 text-[#C5A059] text-sm" data-testid="route-venice-dest-pick-label">
                          Drop-off: <strong>{venicePickDest}</strong>
                        </p>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Specific address toggle on the non-Venice side(s). On a fully
                  non-Venice route this offers the toggle for BOTH ends. */}
              {!originIsVenice && (
                <div className="mt-3" data-testid="route-origin-address-wrap">
                  <SpecificAddressField
                    location={rawDisplayOrigin}
                    value={originOtherAddress}
                    onChange={setOriginOtherAddress}
                    testIdPrefix="route-origin"
                    sideLabel={`the pickup location (${rawDisplayOrigin})`}
                  />
                </div>
              )}
              {!destIsVenice && (
                <div className="mt-3" data-testid="route-dest-address-wrap">
                  <SpecificAddressField
                    location={rawDisplayDestination}
                    value={destOtherAddress}
                    onChange={setDestOtherAddress}
                    testIdPrefix="route-dest"
                    sideLabel={`the drop-off location (${rawDisplayDestination})`}
                  />
                </div>
              )}

              {/* Price Display */}
              <div className="mt-6 text-center">
                <div className="text-sm text-white/50 mb-1">Starting from</div>
                <div className="text-4xl font-bold text-[#C5A059]">
                  €{route.base_price.toFixed(2).replace('.', ',')}
                </div>
                <div className="text-sm text-white/40 mt-1">Mercedes V-Class • Fixed Price</div>
              </div>
            </div>

            {/* Service Features */}
            <div className="bg-[#121212] border border-white/5 rounded-sm p-6">
              <h2 className="text-xl font-semibold text-white mb-6">What's Included</h2>
              
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-[#C5A059] mt-0.5" />
                  <div>
                    <div className="text-white font-medium">2-7 Passengers</div>
                    <div className="text-white/50 text-sm">Comfortable seating for groups</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-[#C5A059] mt-0.5" />
                  <div>
                    <div className="text-white font-medium">7 Bags Included</div>
                    <div className="text-white/50 text-sm">Spacious luggage capacity</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-[#C5A059] mt-0.5" />
                  <div>
                    <div className="text-white font-medium">Fixed Price</div>
                    <div className="text-white/50 text-sm">No hidden fees or surge pricing</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-[#C5A059] mt-0.5" />
                  <div>
                    <div className="text-white font-medium">24/7 Service</div>
                    <div className="text-white/50 text-sm">Available any time, day or night</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-[#C5A059] mt-0.5" />
                  <div>
                    <div className="text-white font-medium">Professional Chauffeur</div>
                    <div className="text-white/50 text-sm">Licensed, experienced drivers</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-[#C5A059] mt-0.5" />
                  <div>
                    <div className="text-white font-medium">Free Cancellation</div>
                    <div className="text-white/50 text-sm">Up to 24h before pickup</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Why Choose Us */}
            <div className="bg-[#121212] border border-white/5 rounded-sm p-6">
              <h2 className="text-xl font-semibold text-white mb-6">Why Choose TaxiNearMe</h2>
              
              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-[#C5A059]/10 flex items-center justify-center flex-shrink-0">
                    <Shield className="w-5 h-5 text-[#C5A059]" />
                  </div>
                  <div>
                    <div className="text-white font-medium">Safe & Reliable</div>
                    <div className="text-white/50 text-sm">All vehicles are regularly maintained and sanitized. Our drivers are fully licensed and insured.</div>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-[#C5A059]/10 flex items-center justify-center flex-shrink-0">
                    <Sparkles className="w-5 h-5 text-[#C5A059]" />
                  </div>
                  <div>
                    <div className="text-white font-medium">Luxury Experience</div>
                    <div className="text-white/50 text-sm">Travel in comfort with our premium Mercedes fleet. Complimentary water and WiFi on board.</div>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-[#C5A059]/10 flex items-center justify-center flex-shrink-0">
                    <Star className="w-5 h-5 text-[#C5A059]" />
                  </div>
                  <div>
                    <div className="text-white font-medium">5-Star Rated Service</div>
                    <div className="text-white/50 text-sm">Thousands of satisfied customers. Check our reviews and see why travelers trust us.</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Sidebar - Booking Form */}
          <div className="lg:col-span-2">
            <div className="booking-widget rounded-sm p-6 sticky top-24" data-testid="booking-form">
              <h3 className="text-xl font-semibold text-white mb-6">Book This Transfer</h3>

              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Date */}
                <div>
                  <label className="form-label flex items-center gap-2">
                    <CalendarIcon className="w-4 h-4 text-[#C5A059]" />
                    Date *
                  </label>
                  <Popover open={datePickerOpen} onOpenChange={setDatePickerOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className="input-dark w-full justify-start text-left font-normal"
                        data-testid="date-picker"
                      >
                        {formData.pickupDate ? format(formData.pickupDate, "dd MMM yyyy") : "Select date"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0 bg-[#1F1F1F] border-[#333]" align="start">
                      <Calendar
                        mode="single"
                        selected={formData.pickupDate}
                        onSelect={(date) => {
                          setFormData({...formData, pickupDate: date});
                          if (date) setDatePickerOpen(false);
                        }}
                        disabled={(date) => !isDateSelectable(bookingSettings, date)}
                        className="text-white"
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                {/* Time */}
                <div>
                  <label className="form-label flex items-center gap-2">
                    <Clock className="w-4 h-4 text-[#C5A059]" />
                    Time *
                  </label>
                  <Select
                    value={formData.pickupTime}
                    onValueChange={(value) => setFormData({...formData, pickupTime: value})}
                  >
                    <SelectTrigger className="input-dark w-full" data-testid="time-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1F1F1F] border-[#333] max-h-60">
                      {filterSlotsForDate(TIME_SLOTS, bookingSettings, formData.pickupDate).map((time) => (
                        <SelectItem key={time} value={time} className="text-white hover:bg-[#333]">
                          {time}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Cutoff hint */}
                {bookingSettings && (
                  <p className="text-[#C5A059]/80 text-xs -mt-2" data-testid="earliest-pickup-hint">
                    {earliestPickupLabel(bookingSettings)}
                  </p>
                )}

                {/* Night surcharge warning */}
                {isNightService() && (
                  <div className="night-badge" data-testid="night-surcharge-badge">
                    <Moon className="w-3 h-3" />
                    Night surcharge applies (20%)
                  </div>
                )}

                {/* Passengers + Luggage */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="form-label flex items-center gap-2">
                      <Users className="w-4 h-4 text-[#C5A059]" />
                      Passengers *
                    </label>
                    <Select
                      value={formData.passengers.toString()}
                      onValueChange={(value) => setFormData({...formData, passengers: parseInt(value)})}
                    >
                      <SelectTrigger className="input-dark w-full" data-testid="passengers-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[#1F1F1F] border-[#333]">
                        {Array.from({ length: Math.max(1, fleet.max_passengers || 12) }, (_, i) => i + 1).map((num) => (
                          <SelectItem key={num} value={num.toString()} className="text-white hover:bg-[#333]">
                            {num} {num === 1 ? "passenger" : "passengers"}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="form-label flex items-center gap-2">
                      <Briefcase className="w-4 h-4 text-[#C5A059]" />
                      Luggage
                    </label>
                    <Select
                      value={(formData.luggage || 1).toString()}
                      onValueChange={(value) => setFormData({...formData, luggage: parseInt(value)})}
                    >
                      <SelectTrigger className="input-dark w-full" data-testid="luggage-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[#1F1F1F] border-[#333]">
                        {Array.from({ length: Math.max(1, (fleet.max_luggage || 12) + 1) }, (_, i) => i).map((num) => (
                          <SelectItem key={num} value={num.toString()} className="text-white hover:bg-[#333]">
                            {num} {num === 1 ? "bag" : "bags"}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Premium Enhancements (multi-select) — same logic as HomePage:
                    hide options whose linked vehicle can't seat the chosen pax
                    or fit the chosen luggage. */}
                {(() => {
                  const visibleOptions = luxuryOptions.filter((opt) => {
                    const vid = opt.auto_assign_vehicle_id;
                    if (!vid) return true;
                    const veh = (fleet.vehicles || []).find(v => v.vehicle_id === vid);
                    if (!veh) return true;
                    if ((formData.passengers || 1) > (veh.max_passengers || 0)) return false;
                    if ((formData.luggage || 0) > (veh.max_luggage || 0)) return false;
                    return true;
                  });
                  if (formData.luxuryOptionIds?.length) {
                    const visibleIds = new Set(visibleOptions.map(o => o.luxury_option_id));
                    const stale = formData.luxuryOptionIds.filter(id => !visibleIds.has(id));
                    if (stale.length) {
                      setTimeout(() => {
                        setFormData(prev => ({
                          ...prev,
                          luxuryOptionIds: (prev.luxuryOptionIds || []).filter(id => visibleIds.has(id))
                        }));
                      }, 0);
                    }
                  }
                  if (visibleOptions.length === 0) return null;
                  return (
                    <div className="bg-[#1F1F1F]/50 rounded-sm p-4 space-y-2" data-testid="luxury-customizations">
                      <div className="flex items-center gap-2 mb-2">
                        <Star className="w-4 h-4 text-[#C5A059]" />
                        <span className="text-white text-sm font-medium">Premium enhancements <span className="text-white/40 text-xs font-normal">(optional)</span></span>
                      </div>
                      {visibleOptions.map((opt) => {
                        const checked = formData.luxuryOptionIds?.includes(opt.luxury_option_id);
                        return (
                          <label
                            key={opt.luxury_option_id}
                            className={`flex items-start gap-3 p-3 rounded-sm cursor-pointer transition-colors ${checked ? "bg-[#C5A059]/10 border border-[#C5A059]/30" : "bg-[#1F1F1F] border border-transparent hover:border-[#C5A059]/20"}`}
                            data-testid={`luxury-option-${opt.luxury_option_id}`}
                          >
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={(e) => {
                                const ids = new Set(formData.luxuryOptionIds || []);
                                if (e.target.checked) ids.add(opt.luxury_option_id);
                                else ids.delete(opt.luxury_option_id);
                                setFormData({...formData, luxuryOptionIds: Array.from(ids)});
                              }}
                              className="mt-1 accent-[#C5A059] w-4 h-4"
                            />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <span className="text-white text-sm font-medium">{opt.title}</span>
                                <span className="text-[10px] bg-[#C5A059]/15 text-[#C5A059] px-2 py-0.5 rounded-sm">+{opt.surcharge_pct}%</span>
                              </div>
                              {opt.short_description && (
                                <p className="text-white/50 text-xs mt-1">{opt.short_description}</p>
                              )}
                            </div>
                            {opt.full_details && (
                              <button
                                type="button"
                                onClick={(e) => { e.preventDefault(); setLuxuryDetail(opt); }}
                                className="text-[#C5A059] text-xs hover:underline shrink-0"
                                data-testid={`luxury-details-${opt.luxury_option_id}`}
                              >
                                Details
                              </button>
                            )}
                          </label>
                        );
                      })}
                    </div>
                  );
                })()}

                {/* Round Trip Option */}
                <div className={`py-3 px-4 rounded-sm transition-all ${formData.isRoundTrip ? 'bg-[#C5A059]/10 border border-[#C5A059]/30' : 'bg-[#1F1F1F]/50'}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Repeat className={`w-5 h-5 ${formData.isRoundTrip ? 'text-[#C5A059]' : 'text-white/50'}`} />
                      <div>
                        <div className="text-white text-sm font-medium flex items-center gap-2">
                          Round Trip
                          <span className="bg-emerald-500/20 text-emerald-400 text-xs px-2 py-0.5 rounded">-5%</span>
                        </div>
                        <div className="text-white/40 text-xs">Book return & save 5%</div>
                      </div>
                    </div>
                    <Switch
                      checked={formData.isRoundTrip}
                      onCheckedChange={(checked) => setFormData({...formData, isRoundTrip: checked})}
                      data-testid="roundtrip-switch"
                    />
                  </div>

                  {/* Return Date/Time */}
                  {formData.isRoundTrip && (
                    <div className="mt-4 pt-4 border-t border-[#C5A059]/20 space-y-3">
                      <div className="text-xs text-[#C5A059] uppercase tracking-wide mb-2">Return Journey</div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="text-white/50 text-xs mb-1 block">Return Date *</label>
                          <Popover open={returnDatePickerOpen} onOpenChange={setReturnDatePickerOpen}>
                            <PopoverTrigger asChild>
                              <Button
                                variant="outline"
                                className="input-dark w-full justify-start text-left font-normal h-10 text-sm"
                                data-testid="return-date-picker"
                              >
                                {formData.returnDate ? format(formData.returnDate, "dd MMM") : "Select"}
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0 bg-[#1F1F1F] border-[#333]" align="start">
                              <Calendar
                                mode="single"
                                selected={formData.returnDate}
                                onSelect={(date) => {
                                  setFormData({...formData, returnDate: date});
                                  if (date) setReturnDatePickerOpen(false);
                                }}
                                disabled={(date) => date < (formData.pickupDate || new Date())}
                                className="text-white"
                              />
                            </PopoverContent>
                          </Popover>
                        </div>
                        <div>
                          <label className="text-white/50 text-xs mb-1 block">Return Time *</label>
                          <Select
                            value={formData.returnTime}
                            onValueChange={(value) => setFormData({...formData, returnTime: value})}
                          >
                            <SelectTrigger className="input-dark w-full h-10 text-sm" data-testid="return-time-select">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-[#1F1F1F] border-[#333] max-h-60">
                              {TIME_SLOTS.map((time) => (
                                <SelectItem key={time} value={time} className="text-white hover:bg-[#333]">
                                  {time}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Price Breakdown — itemized, matches HomePage */}
                {pricePreview && (
                  <div className="border-t border-white/10 pt-4 mt-4 space-y-2" data-testid="price-preview">
                    <div className="flex justify-between text-sm">
                      <span className="text-white/50">Base price</span>
                      <span className="text-white">€{pricePreview.base_price.toFixed(2)}</span>
                    </div>
                    {formData.isRoundTrip && (
                      <div className="flex justify-between text-sm">
                        <span className="text-white/50">Return trip</span>
                        <span className="text-white">€{pricePreview.base_price.toFixed(2)}</span>
                      </div>
                    )}
                    {pricePreview.water_taxi_surcharge > 0 && (
                      <div className="flex justify-between text-sm" data-testid="price-water-taxi-line">
                        <span className="text-[#C5A059] flex items-center gap-1">
                          Water taxi
                          {pricePreview.water_taxi_legs > 1 && (
                            <span className="text-white/40 text-xs">
                              ({pricePreview.water_taxi_legs}× €{(pricePreview.water_taxi_surcharge / pricePreview.water_taxi_legs).toFixed(2)})
                            </span>
                          )}
                        </span>
                        <span className="text-[#C5A059]">+€{pricePreview.water_taxi_surcharge.toFixed(2)}</span>
                      </div>
                    )}
                    {pricePreview.luxury_breakdown && pricePreview.luxury_breakdown.length > 0 ? (
                      pricePreview.luxury_breakdown.map((lb) => (
                        <div key={lb.luxury_option_id} className="flex justify-between text-sm" data-testid={`price-luxury-line-${lb.luxury_option_id}`}>
                          <span className="text-white/50">{lb.title} (+{lb.surcharge_pct}%)</span>
                          <span className="text-[#C5A059]">+€{lb.amount.toFixed(2)}</span>
                        </div>
                      ))
                    ) : pricePreview.luxury_surcharge > 0 ? (
                      <div className="flex justify-between text-sm">
                        <span className="text-white/50">Luxury (+5%)</span>
                        <span className="text-[#C5A059]">+€{pricePreview.luxury_surcharge.toFixed(2)}</span>
                      </div>
                    ) : null}
                    {pricePreview.passenger_surcharge > 0 && (
                      <div className="flex justify-between text-sm" data-testid="price-pax-surcharge">
                        <span className="text-white/50 flex items-center gap-1">
                          <Users className="w-3 h-3" /> Group surcharge{pricePreview.passenger_bracket_label ? ` (${pricePreview.passenger_bracket_label})` : ""}
                        </span>
                        <span className="text-[#C5A059]">+€{pricePreview.passenger_surcharge.toFixed(2)}</span>
                      </div>
                    )}
                    {pricePreview.is_night_service && (
                      <div className="flex justify-between text-sm">
                        <span className="text-white/50 flex items-center gap-1">
                          <Moon className="w-3 h-3" /> Night surcharge
                        </span>
                        <span className="text-[#C5A059]">+€{pricePreview.night_surcharge.toFixed(2)}</span>
                      </div>
                    )}
                    {formData.isRoundTrip && pricePreview.round_trip_discount > 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-emerald-400 flex items-center gap-1">
                          <Percent className="w-3 h-3" /> Round trip discount
                        </span>
                        <span className="text-emerald-400">-€{pricePreview.round_trip_discount.toFixed(2)}</span>
                      </div>
                    )}
                    {(() => {
                      const baseTotal = formData.isRoundTrip
                        ? pricePreview.base_price * 2
                        : pricePreview.base_price;
                      const extras = pricePreview.total_price - baseTotal;
                      const extrasFmt = `${extras < 0 ? "-" : "+"}€${Math.abs(extras).toFixed(2)}`;
                      return (
                        <>
                          <div className="flex justify-between pt-3 mt-1 border-t border-white/10 text-sm" data-testid="price-formula">
                            <span className="text-white/60">Base €{baseTotal.toFixed(2)} {extras !== 0 ? `+ Extras ${extrasFmt}` : ""}</span>
                            <span className="text-white/60">=</span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-white font-semibold uppercase tracking-wide text-sm">Total</span>
                            <span className="text-2xl font-bold text-[#C5A059]" data-testid="route-total-price">€{pricePreview.total_price.toFixed(2)}</span>
                          </div>
                        </>
                      );
                    })()}
                    {formData.isRoundTrip && pricePreview.round_trip_discount > 0 && (
                      <div className="text-center text-emerald-400 text-xs mt-1">
                        You save €{pricePreview.round_trip_discount.toFixed(2)} with round trip!
                      </div>
                    )}
                  </div>
                )}

                {/* Contact Info */}
                <div className="border-t border-white/10 pt-4 mt-4">
                  <h4 className="text-sm text-white/50 uppercase tracking-wide mb-4">Contact Information</h4>
                  <div className="space-y-3">
                    <Input
                      placeholder="Full Name *"
                      value={formData.userName}
                      onChange={(e) => setFormData({...formData, userName: e.target.value})}
                      className="input-dark"
                      data-testid="name-input"
                    />
                    <Input
                      type="email"
                      placeholder="Email *"
                      value={formData.userEmail}
                      onChange={(e) => setFormData({...formData, userEmail: e.target.value})}
                      className="input-dark"
                      data-testid="email-input"
                    />
                    <div>
                      <Input
                        type="tel"
                        placeholder="Phone Number * (e.g. +39 393 907 8663)"
                        value={formData.userPhone}
                        onChange={(e) => setFormData({...formData, userPhone: e.target.value})}
                        className={
                          "input-dark " +
                          (formData.userPhone && !isValidInternationalPhone(formData.userPhone)
                            ? "border-red-500/60"
                            : "")
                        }
                        data-testid="phone-input"
                        aria-invalid={formData.userPhone ? !isValidInternationalPhone(formData.userPhone) : undefined}
                      />
                      {formData.userPhone && !isValidInternationalPhone(formData.userPhone) && (
                        <p className="text-red-400 text-xs mt-1" data-testid="phone-error">
                          {INTERNATIONAL_PHONE_HINT}
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                <FlightDetailsFields
                  value={formData.flightDetails}
                  onChange={(v) => setFormData({...formData, flightDetails: v})}
                />

                <div>
                  <label className="text-white/50 text-[10px] uppercase mb-1 block">Note (optional)</label>
                  <textarea
                    rows={2}
                    maxLength={1000}
                    placeholder="Anything we should know? (luggage, child seat, special requests...)"
                    value={formData.notes}
                    onChange={(e) => setFormData({...formData, notes: e.target.value})}
                    className="w-full bg-[#1A1A1A] border border-[#333] rounded-sm p-2 text-white/80 text-sm resize-none focus:border-[#C5A059]/50 focus:outline-none"
                    data-testid="note-input"
                  />
                </div>

                <PrivacyCheckbox
                  checked={formData.privacyAccepted}
                  onChange={(v) => setFormData({...formData, privacyAccepted: v})}
                />

                <TermsCheckbox
                  checked={formData.termsAccepted}
                  onChange={(v) => setFormData({...formData, termsAccepted: v})}
                />

                {/* Submit */}
                <Button
                  type="submit"
                  className="btn-gold w-full mt-6 py-3 h-auto leading-tight"
                  disabled={submitting}
                  data-testid="submit-quote-btn"
                >
                  {submitting ? (
                    <span className="flex items-center gap-2">
                      <div className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                      Processing...
                    </span>
                  ) : (
                    <span className="flex flex-col items-center">
                      <span className="text-sm font-semibold tracking-wide uppercase">Send Request</span>
                      <span className="text-[10px] font-normal opacity-70 normal-case mt-0.5">we'll get back to you in a few minutes</span>
                    </span>
                  )}
                </Button>
                
                <p className="text-xs text-white/40 text-center">
                  No payment required. We'll confirm availability.
                </p>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#0A0A0A] border-t border-white/5 py-8 mt-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="text-2xl font-bold logo-text">TaxiNearMe</div>
            <div className="flex items-center gap-6 text-white/40 text-sm">
              <a href={`tel:${(t.footer_contact_phone || "").replace(/\s+/g, "")}`} className="flex items-center gap-2 hover:text-[#C5A059]">
                <Phone className="w-4 h-4" /> {t.footer_contact_phone}
              </a>
              <a href={`mailto:${t.footer_contact_email}`} className="flex items-center gap-2 hover:text-[#C5A059]">
                <Mail className="w-4 h-4" /> {t.footer_contact_email}
              </a>
            </div>
          </div>
          <div className="mt-4 flex flex-col md:flex-row gap-3 items-center justify-between text-white/30 text-sm">
            <div>© 2026 TaxiNearMe. Premium transfers from Venice.</div>
            <div className="flex items-center gap-4 flex-wrap justify-center" data-testid="footer-legal-links">
              <Link to="/privacy" className="hover:text-[#C5A059] transition-colors">Privacy Policy</Link>
              <span className="text-white/15">•</span>
              <Link to="/legal/cookies" className="hover:text-[#C5A059] transition-colors">Cookie Policy</Link>
              <span className="text-white/15">•</span>
              <Link to="/legal/terms" className="hover:text-[#C5A059] transition-colors">Terms</Link>
              <span className="text-white/15">•</span>
              <button type="button" onClick={() => window.dispatchEvent(new Event("tnm-open-cookie-preferences"))} className="hover:text-[#C5A059] transition-colors">
                Cookie preferences
              </button>
            </div>
          </div>
        </div>
      </footer>

      {/* Luxury option details popup (mirrors HomePage) */}
      {luxuryDetail && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
          onClick={() => setLuxuryDetail(null)}
          data-testid="luxury-detail-modal"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-[#0F0F0F] border border-white/10 rounded-sm w-full max-w-lg max-h-[85vh] overflow-y-auto"
          >
            <div className="flex items-center justify-between p-5 border-b border-white/5">
              <div>
                <h3 className="text-lg font-semibold text-white">{luxuryDetail.title}</h3>
                <p className="text-[#C5A059] text-xs mt-1">+{luxuryDetail.surcharge_pct}% surcharge</p>
              </div>
              <button
                type="button"
                onClick={() => setLuxuryDetail(null)}
                className="text-white/40 hover:text-white text-2xl leading-none"
                data-testid="luxury-detail-close"
              >
                ×
              </button>
            </div>
            {luxuryDetail.image_url && (
              <div className="aspect-video w-full overflow-hidden bg-[#050505]" data-testid="luxury-detail-image">
                <img
                  src={luxuryDetail.image_url.startsWith("/api/")
                    ? `${process.env.REACT_APP_BACKEND_URL}${luxuryDetail.image_url}`
                    : luxuryDetail.image_url}
                  alt={luxuryDetail.title}
                  className="w-full h-full object-cover"
                  onError={(e) => { e.currentTarget.parentElement.style.display = "none"; }}
                />
              </div>
            )}
            <article
              className="p-6 text-white/80 prose-custom prose-sm"
              dangerouslySetInnerHTML={{ __html: renderMarkdownSafe(luxuryDetail.full_details, "luxury-detail") }}
            />
          </div>
        </div>
      )}
    </div>
  );
}
